import supabase                         from '../utils/supabase.js'
import { guardApp, navigate }           from '../router.js'
import { getAvailablePresets, getPreset, createPreset } from '../presets/registry.js'
import AudioAnalyser                    from '../Experience/World/AudioAnalyser.js'
import ExportPreviewOverlay             from '../ExportPreviewOverlay.js'

export default class App {
    constructor() {
        this._session      = null
        this._profile      = null
        this._activePreset = null
        this._analyser     = new AudioAnalyser()
        this._rafId        = null
        this._startTime    = performance.now()

        // Recording state
        this._isRecording  = false
        this._recorder     = null
        this._chunks       = []
        this._audioDest    = null
        this._recTimer     = null
        this._recElapsed   = 0
        this._recPreset    = null   // fresh preset instance for offscreen rendering (WebGL1 presets only)
        this._recCanvas    = null   // WebGL offscreen canvas at target dims (WebGL1 presets only)
        this._compCanvas   = null   // 2D composite canvas (WebGL + watermark)
        this._compCtx      = null
        this._recW              = null   // target recording width
        this._recH              = null   // target recording height
        this._recCameraZRestore = null   // { preset, z } — restore after recording for void/particles

        // Config (Creator/Pro customization)
        this._presetConfigs   = {}
        this._configSaveTimer = null
        this._exportsData     = null

        this._init()
    }

    async _ensureProfile(session) {
        const { data } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', session.user.id)
            .single()

        if (!data) {
            await supabase.from('profiles').insert({
                id:    session.user.id,
                email: session.user.email,
                plan:  'free'
            })
        }
    }

    async _init() {
        const { data: { session } } = await supabase.auth.getSession()
        this._session = session

        if (session) {
            await this._ensureProfile(session)
            this._profile     = await this._loadProfile()
            this._exportsData = await this._loadExports()
            this._presetConfigs = this._profile.config ?? {}
            if (!Object.keys(this._presetConfigs).length) {
                try {
                    const cached = localStorage.getItem('vyzflow_config')
                    if (cached) this._presetConfigs = JSON.parse(cached)
                } catch (_) {}
            }
        } else {
            this._profile     = { plan: 'pro', email: '', config: {} }
            this._exportsData = { history: [], monthlyCount: 0 }
            try {
                const cached = localStorage.getItem('vyzflow_config')
                if (cached) this._presetConfigs = JSON.parse(cached)
            } catch (_) {}
        }

        this._render()
        this._bind()
        this._startLoop()
    }

    async _loadExports() {
        const uid = this._session.user.id
        const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()

        const [historyRes, countRes] = await Promise.all([
            supabase
                .from('exports')
                .select('id, preset_id, format, created_at')
                .eq('user_id', uid)
                .order('created_at', { ascending: false })
                .limit(10),
            supabase
                .from('exports')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', uid)
                .gte('created_at', monthStart)
        ])

        return {
            history:      historyRes.data ?? [],
            monthlyCount: countRes.count  ?? 0
        }
    }

    async _loadProfile() {
        const { data } = await supabase
            .from('profiles')
            .select('plan, email, config')
            .eq('id', this._session.user.id)
            .single()
        return data ?? { plan: 'free', email: this._session.user.email, config: {} }
    }

    _render() {
        const email   = this._session?.user?.email ?? ''
        const plan    = this._profile.plan ?? 'pro'
        const presets = getAvailablePresets(plan)

        const urlPreset = new URLSearchParams(location.search).get('preset')
        const firstId   = (urlPreset && presets.find(p => p.id === urlPreset))
            ? urlPreset
            : (presets[0]?.id ?? '')

        const { history, monthlyCount } = this._exportsData

        const headerUser = email
            ? `<span class="app-email">${email}</span>
               <span class="app-plan app-plan--${plan}">${plan}</span>
               <button class="app-signout" id="signOutBtn">Sign Out</button>`
            : `<span class="app-plan app-plan--${plan}">${plan}</span>
               <a class="app-signout" href="/login">Sign In</a>`

        const chevron = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`

        document.body.innerHTML = `
<div class="app-page">
    <header class="app-header">
        <a href="/" class="logo-link"><div class="app-logo">vyz<span>flow</span></div></a>
        <div class="app-user">${headerUser}</div>
    </header>

    <main class="app-main">
        <aside class="app-sidebar">

            <section class="app-section app-section--accordion" id="presetsSection">
                <div class="app-section-header" id="presetsHeader">
                    <h2 class="app-section-title">Presets</h2>
                    <button class="app-section-toggle" id="presetsToggle" aria-expanded="true">${chevron}</button>
                </div>
                <div class="app-section-body">
                    <div class="app-preset-list">
                        ${presets.map(p => `
                        <div class="app-preset-item${p.id === firstId ? ' app-preset-item--active' : ''}" data-preset="${p.id}">
                            <span class="app-preset-name">${p.name}</span>
                            ${p.plan !== 'free' ? `<span class="app-preset-badge">${p.plan}</span>` : ''}
                        </div>`).join('')}
                    </div>
                </div>
            </section>

            <section class="app-section app-section--config" id="configPanel"></section>

            <section class="app-section" id="audioSection">
                <h2 class="app-section-title">Audio</h2>
                <label class="app-upload-btn" for="audioFile" id="audioUploadLabel">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    Upload audio
                </label>
                <input type="file" id="audioFile" accept=".mp3,.wav,.flac,.ogg,.aac,.m4a,audio/*" class="app-upload-input">
                <div class="app-file-name" id="fileName">No file selected</div>

                <div class="app-player" id="audioPlayer" hidden>
                    <button class="app-play-btn" id="playBtn">Play</button>
                    <div class="app-progress-bar" id="progressBar">
                        <div class="app-progress-fill" id="progressFill"></div>
                    </div>
                    <div class="app-time" id="timeDisplay">0:00 / 0:00</div>
                </div>
            </section>

            <section class="app-section" id="exportSection">
                <h2 class="app-section-title">Export Video</h2>
                <p class="app-export-desktop-msg">To export — go to the desktop version of the site.</p>
                <div class="app-export-main">
                    ${plan === 'free' ? this._renderExportCounter(monthlyCount) : ''}
                    <div class="app-export-btns">
                        <button class="app-export-btn" id="appExport9x16" data-format="9x16"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 4v13M6 11l6 6 6-6"/></svg>9:16</button>
                        <button class="app-export-btn${plan === 'free' ? ' app-export-btn--locked' : ''}" id="appExport16x9" data-format="16x9" title="${plan === 'free' ? 'Creator plan required' : ''}"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 4v13M6 11l6 6 6-6"/></svg>16:9</button>
                        <button class="app-export-btn${plan === 'free' ? ' app-export-btn--locked' : ''}" id="appExport1x1"  data-format="1x1"  title="${plan === 'free' ? 'Creator plan required' : ''}"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 4v13M6 11l6 6 6-6"/></svg>1:1</button>
                    </div>
                    <div class="app-rec-bar" id="appRecBar" hidden>
                        <span class="app-rec-dot"></span>
                        <span id="appRecTime">0:00</span>
                        <button class="app-stop-btn" id="appStopBtn">Stop</button>
                    </div>
                    <p class="app-export-msg" id="appExportMsg"></p>
                    ${!email ? `<p class="app-signin-hint">To save your export history, please <a href="/login" class="app-signin-link">Sign In</a>.</p>` : ''}
                    <div id="exportHistory">${this._renderExportHistory(history)}</div>
                </div>
            </section>

        </aside>

        <div class="app-canvas-area" id="canvasArea">
            <canvas class="app-canvas" id="presetCanvas"></canvas>
        </div>
    </main>
</div>

<div class="email-modal-overlay" id="emailModalOverlay" hidden>
    <div class="email-modal">
        <p class="email-modal-title">Enter your email to export</p>
        <input class="email-modal-input" id="emailModalInput" type="email" placeholder="your@email.com" autocomplete="email">
        <div class="email-modal-btns">
            <button class="email-modal-cancel" id="emailModalCancel">Cancel</button>
            <button class="email-modal-submit" id="emailModalSubmit">Export</button>
        </div>
    </div>
</div>`

        // Collapse presets by default on mobile
        if (window.innerWidth <= 680) {
            document.getElementById('presetsSection')?.classList.add('collapsed')
            document.getElementById('presetsToggle')?.setAttribute('aria-expanded', 'false')
        }

        requestAnimationFrame(() => {
            this._sizeCanvas()
            if (firstId) this._switchPreset(firstId)
        })
    }

    _bind() {
        document.getElementById('signOutBtn')?.addEventListener('click', async () => {
            await supabase.auth.signOut()
            navigate('/')
        })

        document.querySelectorAll('.app-preset-item').forEach(el => {
            el.addEventListener('click', () => this._switchPreset(el.dataset.preset))
        })

        const playBtn = document.getElementById('playBtn')
        // prewarm must run in mousedown/touchstart — trusted gesture for AudioContext
        playBtn.addEventListener('mousedown',  () => this._analyser.prewarm())
        playBtn.addEventListener('touchstart', () => this._analyser.prewarm(), { passive: true })
        playBtn.addEventListener('click', () => this._togglePlay())

        document.getElementById('audioFile').addEventListener('change', (e) => {
            const file = e.target.files[0]
            if (!file) return
            this._analyser.load(file)
            document.getElementById('fileName').textContent = file.name
        })

        this._analyser.on('ready', () => {
            document.getElementById('audioPlayer').hidden = false
            document.getElementById('playBtn').textContent = 'Play'
        })

        this._analyser.on('ended', () => {
            document.getElementById('playBtn').textContent = 'Play'
        })

        document.getElementById('progressBar').addEventListener('click', (e) => {
            const rect = e.currentTarget.getBoundingClientRect()
            const pct  = (e.clientX - rect.left) / rect.width
            this._analyser.seek(pct * this._analyser.duration)
        })

        window.addEventListener('resize', () => this._sizeCanvas())

        document.querySelectorAll('.app-export-btn').forEach(btn => {
            btn.addEventListener('click', () => this._triggerExport(btn.dataset.format))
        })
        document.getElementById('appStopBtn')?.addEventListener('click', () => this._stopRecording())

        // Presets accordion toggle (mobile only via CSS visibility)
        document.getElementById('presetsHeader')?.addEventListener('click', () => {
            const section = document.getElementById('presetsSection')
            const toggle  = document.getElementById('presetsToggle')
            const collapsed = section.classList.toggle('collapsed')
            toggle.setAttribute('aria-expanded', !collapsed)
        })

        // Prewarm AudioContext when user taps the upload label (before file picker opens)
        const uploadLabel = document.getElementById('audioUploadLabel')
        uploadLabel?.addEventListener('touchstart', () => this._analyser.prewarm(), { passive: true })
        uploadLabel?.addEventListener('mousedown',  () => this._analyser.prewarm())

        this._exportOverlay = new ExportPreviewOverlay({
            onRecord:     (format, scaleConfig) => this._startRecording(format, scaleConfig),
            onStop:       () => this._stopRecording(),
            onDeactivate: () => this._sizeCanvas()
        })
    }

    _togglePlay() {
        this._analyser.toggle()
        document.getElementById('playBtn').textContent = this._analyser.isPlaying ? 'Pause' : 'Play'
    }

    _switchPreset(id) {
        if (this._exportOverlay?.isActive()) this._exportOverlay.deactivate()
        if (this._activePreset) this._activePreset.destroy()
        
        document.querySelectorAll('.app-preset-item').forEach(el => {
            el.classList.toggle('app-preset-item--active', el.dataset.preset === id)
        })

        const canvas = this._freshCanvas()
        const preset = getPreset(id)
        if (!preset) return

        preset.init(canvas)
        this._activePreset = preset

        if (preset.compile) preset.compile()

        this._applyPresetConfig(id)
        this._updateConfigPanel(id)
    }

    _freshCanvas() {
        const old = document.getElementById('presetCanvas')
        const c   = document.createElement('canvas')
        c.className = 'app-canvas'
        c.id        = 'presetCanvas'
        if (old) {
            c.style.cssText = old.style.cssText
            c.width         = old.width
            c.height        = old.height
            old.replaceWith(c)
        } else {
            document.getElementById('canvasArea')?.appendChild(c)
        }
        return c
    }

    _sizeCanvas() {
        if (this._exportOverlay?.isActive()) return
        const area   = document.getElementById('canvasArea')
        const canvas = document.getElementById('presetCanvas')
        if (!area || !canvas) return

        const isMobile   = window.innerWidth <= 680
        const dpr        = Math.min(window.devicePixelRatio, 2)
        const containerW = area.clientWidth
        const containerH = area.clientHeight

        let width, height
        if (isMobile) {
            // Container CSS is already calc(100vw * 16/9) — fill it completely
            width  = containerW
            height = containerH || width * (16 / 9)
        } else {
            // Landscape 16:9 for desktop
            width  = containerW
            height = width * (9 / 16)
            if (height > containerH) {
                height = containerH
                width  = height * (16 / 9)
            }
        }

        canvas.style.width  = Math.floor(width) + 'px'
        canvas.style.height = Math.floor(height) + 'px'
        canvas.width  = Math.round(width * dpr)
        canvas.height = Math.round(height * dpr)

        if (this._activePreset?.resize) {
            this._activePreset.resize(canvas.width, canvas.height)
        }
    }

    _startLoop() {
        const tick = () => {
            const time = (performance.now() - this._startTime) / 1000

            let bass, mid, treble, audioLevel
            if (this._analyser.isPlaying) {
                this._analyser.update()
                bass       = this._analyser.bass
                mid        = this._analyser.mid
                treble     = this._analyser.high
                audioLevel = this._analyser.audioLevel
                this._updateProgress()
            } else {
                bass       = Math.pow(Math.abs(Math.sin(time * 0.9)), 3.0) * 0.05
                mid        = Math.pow(Math.abs(Math.sin(time * 1.1)), 2.0) * 0.05
                treble     = Math.pow(Math.abs(Math.sin(time * 1.8)), 2.0) * 0.05
                audioLevel = bass
            }

            this._activePreset?.tick({ time, bass, mid, treble, audioLevel })

            // Offscreen recording
            if (this._isRecording && this._compCtx) {
                if (this._recPreset && this._recCanvas) {
                    // WebGL1 presets: re-render at target resolution
                    this._recPreset.tick({ time, bass, mid, treble, audioLevel })
                    this._compCtx.drawImage(this._recCanvas, 0, 0)
                } else {
                    // Three.js presets (VoidPreset): copy directly from live canvas.
                    // The active preset already rendered this frame above; preserveDrawingBuffer
                    // keeps the pixel data available for drawImage without a second render pass.
                    // This avoids bloom resolution mismatch from re-rendering at a different size.
                    const liveCanvas = this._activePreset?._renderer?.domElement
                    if (liveCanvas) {
                        const tw = this._recW, th = this._recH
                        const liveAspect = liveCanvas.width / liveCanvas.height
                        const targetAspect = tw / th
                        if (Math.abs(liveAspect - targetAspect) < 0.01) {
                            this._compCtx.drawImage(liveCanvas, 0, 0, tw, th)
                        } else if (liveAspect > targetAspect) {
                            // live is wider → center-crop horizontally (matches CSS overflow:hidden preview)
                            const sw = Math.round(liveCanvas.height * targetAspect)
                            const sx = Math.round((liveCanvas.width - sw) / 2)
                            this._compCtx.drawImage(liveCanvas, sx, 0, sw, liveCanvas.height, 0, 0, tw, th)
                        } else {
                            // live is taller → center-crop vertically
                            const sh = Math.round(liveCanvas.width / targetAspect)
                            const sy = Math.round((liveCanvas.height - sh) / 2)
                            this._compCtx.drawImage(liveCanvas, 0, sy, liveCanvas.width, sh, 0, 0, tw, th)
                        }
                    }
                }
                if ((this._profile?.plan ?? 'pro') === 'free') {
                    this._drawWatermark(this._compCtx, this._compCanvas.width, this._compCanvas.height, time)
                }
            }

            this._rafId = requestAnimationFrame(tick)
        }
        this._rafId = requestAnimationFrame(tick)
    }

    _updateProgress() {
        const fill    = document.getElementById('progressFill')
        const display = document.getElementById('timeDisplay')
        if (!fill || !display) return
        const cur = this._analyser.currentTime
        const dur = this._analyser.duration
        if (dur > 0) fill.style.width = (cur / dur * 100) + '%'
        display.textContent = `${this._fmt(cur)} / ${this._fmt(dur)}`
    }

    _fmt(s) {
        const m   = Math.floor(s / 60)
        const sec = Math.floor(s % 60).toString().padStart(2, '0')
        return `${m}:${sec}`
    }

    // ── Recording ────────────────────────────────────────────────────────────

    async _triggerExport(format) {
        if (this._isRecording) return

        if (this._exportOverlay.isActive()) {
            if (this._exportOverlay.activeFormat === format) {
                this._exportOverlay.deactivate()
                return
            }
            this._exportOverlay.deactivate()
        }

        const email = await this._ensureEmail()
        if (!email) return

        const activePresetId = document.querySelector('.app-preset-item--active')?.dataset.preset ?? 'void'
        this._exportOverlay.activate(
            format,
            activePresetId,
            this._presetConfigs[activePresetId] ?? {},
            this._activePreset
        )
    }

    _startRecording(format, scaleConfig = null) {
        const DIMS = { '9x16': [1080, 1920], '16x9': [1920, 1080], '1x1': [1080, 1080] }
        const [w, h] = DIMS[format] || DIMS['9x16']

        const activeId = document.querySelector('.app-preset-item--active')?.dataset.preset ?? 'void'
        this._recPresetId = activeId
        this._recW = w
        this._recH = h

        // Apply Camera Z from preview modal to the live preset before recording starts.
        // void/particles copy from the live canvas, so we must modify their live camera.
        if (scaleConfig?.cameraZ !== undefined && this._activePreset?._camera) {
            this._recCameraZRestore = {
                preset: this._activePreset,
                z:      this._activePreset._camera.position.z
            }
            this._activePreset._camera.position.z = scaleConfig.cameraZ
        }

        // Three.js presets (e.g. VoidPreset) own their renderer and use EffectComposer
        // with resolution-dependent bloom. Re-rendering at a different target size causes
        // visible quality differences. Instead, copy frames from the live canvas directly.
        // WebGL1 presets use a separate offscreen canvas because they have no live renderer.
        const isThreePreset = !!this._activePreset?._renderer

        if (!isThreePreset) {
            // ── WebGL1 presets: separate offscreen canvas at target resolution ──
            const recCanvas = document.createElement('canvas')
            recCanvas.width = w;  recCanvas.height = h
            recCanvas.style.cssText = 'position:fixed;left:-9999px;top:0'
            document.body.appendChild(recCanvas)
            this._recPreset = createPreset(activeId)
            this._recPreset.init(recCanvas)
            // Apply saved config, then override with preview scale if present
            this._recPreset.setConfig({ ...(this._presetConfigs[activeId] ?? {}), ...(scaleConfig ?? {}) })
            this._recCanvas = recCanvas
        }

        // ── Composite 2D canvas: WebGL frame + optional watermark ─────────
        const compCanvas = document.createElement('canvas')
        compCanvas.width = w;  compCanvas.height = h
        compCanvas.style.cssText = 'position:fixed;left:-9998px;top:0'
        document.body.appendChild(compCanvas)
        this._compCanvas = compCanvas
        this._compCtx    = compCanvas.getContext('2d')

        // ── Audio stream ──────────────────────────────────────────────────
        const videoStream = compCanvas.captureStream(30)
        let stream = videoStream
        if (this._analyser._ctx && this._analyser._analyser) {
            this._audioDest = this._analyser._ctx.createMediaStreamDestination()
            this._analyser._analyser.connect(this._audioDest)
            stream = new MediaStream([...videoStream.getTracks(), ...this._audioDest.stream.getTracks()])
        }

        // ── MediaRecorder ─────────────────────────────────────────────────
        const mimes = ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm']
        const mime  = mimes.find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm'
        this._chunks   = []
        this._recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 })
        this._recorder.ondataavailable = e => { if (e.data.size > 0) this._chunks.push(e.data) }
        this._recorder.onstop = () => this._finalizeRecording(format, mime)
        this._recorder.start(100)

        this._isRecording = true
        this._recElapsed  = 0
        this._setRecUI(true)
        this._recTimer = setInterval(() => {
            this._recElapsed++
            const m = Math.floor(this._recElapsed / 60)
            const s = String(this._recElapsed % 60).padStart(2, '0')
            const el = document.getElementById('appRecTime')
            if (el) el.textContent = `${m}:${s}`
        }, 1000)
    }

    _stopRecording() {
        if (!this._isRecording) return
        this._recorder?.stop()
        clearInterval(this._recTimer)
        this._isRecording = false
        if (this._audioDest) {
            this._analyser._analyser?.disconnect(this._audioDest)
            this._audioDest = null
        }

        // Restore live camera Z that was temporarily changed for void/particles recording
        if (this._recCameraZRestore?.preset?._camera) {
            this._recCameraZRestore.preset._camera.position.z = this._recCameraZRestore.z
        }
        this._recCameraZRestore = null

        this._recPreset?.destroy()
        this._recPreset = null
        this._recCanvas?.remove()
        this._compCanvas?.remove()
        this._recCanvas  = null
        this._compCanvas = null
        this._compCtx    = null
        this._recW       = null
        this._recH       = null
        this._setRecUI(false)
        this._exportOverlay?.deactivate()
    }

    async _finalizeRecording(format, mimeType) {
        const ext      = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
        const presetId = this._recPresetId ?? 'void'
        const name     = `vyzflow-${presetId}-${format}-${Date.now()}.${ext}`

        const blob = new Blob(this._chunks, { type: mimeType })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href = url;  a.download = name;  a.click()
        URL.revokeObjectURL(url)
        this._chunks = []

        const email = localStorage.getItem('vyzflow_email') ?? ''
        if (this._session) {
            await supabase.from('exports').insert({
                user_id:   this._session.user.id,
                preset_id: presetId,
                format
            }).then(({ error }) => { if (error) console.warn('export log failed:', error) })
        }
        if (email) {
            await supabase.from('email_leads').insert({ email, preset_id: presetId, format })
                .then(() => {})
        }
    }

    _drawWatermark(ctx, width, height, time) {
        const px      = 0.5 + 0.30 * Math.sin(time * 0.023)
        const py      = 0.5 + 0.35 * Math.cos(time * 0.017)
        const opacity = 0.20 + 0.08 * Math.sin(time * 0.11)
        const fontSize = Math.max(18, Math.round(width * 0.021))
        ctx.save()
        ctx.font = `400 ${fontSize}px 'Tenor Sans', sans-serif`
        if ('letterSpacing' in ctx) ctx.letterSpacing = '3px'
        ctx.textAlign    = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle    = `rgba(237,186,151,${opacity.toFixed(3)})`
        ctx.fillText('vyzflow.com', px * width, py * height)
        ctx.restore()
    }

    _setRecUI(active) {
        const bar  = document.getElementById('appRecBar')
        const btns = document.querySelectorAll('.app-export-btn')
        // Hide the rec bar when the export overlay owns the Stop button
        if (bar)  bar.hidden = !active || this._exportOverlay?.isActive()
        btns.forEach(b => b.disabled = active)
        if (!active) {
            const el = document.getElementById('appRecTime')
            if (el) el.textContent = '0:00'
        }
    }

    _showExportMsg(msg) {
        const el = document.getElementById('appExportMsg')
        if (!el) return
        el.textContent = msg
        el.classList.add('visible')
        setTimeout(() => el.classList.remove('visible'), 4000)
    }

    async _ensureEmail() {
        const stored = localStorage.getItem('vyzflow_email')
        if (stored) return stored
        return new Promise(resolve => {
            const overlay = document.getElementById('emailModalOverlay')
            const input   = document.getElementById('emailModalInput')
            const submit  = document.getElementById('emailModalSubmit')
            const cancel  = document.getElementById('emailModalCancel')
            overlay.hidden = false
            input.value = ''
            input.focus()

            const onSubmit = () => {
                const val = input.value.trim()
                if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                    input.classList.add('email-modal-input--error')
                    setTimeout(() => input.classList.remove('email-modal-input--error'), 800)
                    return
                }
                localStorage.setItem('vyzflow_email', val)
                overlay.hidden = true
                submit.removeEventListener('click', onSubmit)
                cancel.removeEventListener('click', onCancel)
                resolve(val)
            }
            const onCancel = () => {
                overlay.hidden = true
                submit.removeEventListener('click', onSubmit)
                cancel.removeEventListener('click', onCancel)
                resolve(null)
            }
            submit.addEventListener('click', onSubmit)
            cancel.addEventListener('click', onCancel)
            input.addEventListener('keydown', (e) => { if (e.key === 'Enter') onSubmit() }, { once: true })
        })
    }

    _renderExportCounter(monthlyCount) {
        const limit = 3
        const used  = Math.min(monthlyCount, limit)
        const pct   = Math.round((used / limit) * 100)
        return `
        <div class="app-export-counter">
            <span class="app-export-counter-text">${used} of ${limit} exports used this month</span>
            <div class="app-export-bar">
                <div class="app-export-bar-fill" style="width:${pct}%"></div>
            </div>
        </div>`
    }

    // ── Config customization (Creator / Pro) ─────────────────────────────────

    _applyPresetConfig(presetId) {
        const cfg = this._presetConfigs[presetId] ?? {}
        this._activePreset?.setConfig(cfg)
    }

    _updateConfigPanel(presetId) {
        const plan   = this._profile?.plan ?? 'free'
        const preset = this._activePreset
        const panel  = document.getElementById('configPanel')
        if (!panel || !preset) return
        panel.innerHTML = this._renderConfigPanel(preset, plan, presetId)

        if (panel.innerHTML) {
            panel.classList.add('app-section--accordion')
            if (window.innerWidth <= 680) {
                panel.classList.add('collapsed')
                panel.querySelector('.app-section-toggle')?.setAttribute('aria-expanded', 'false')
            }
            panel.querySelector('.app-section-header')?.addEventListener('click', () => {
                const collapsed = panel.classList.toggle('collapsed')
                panel.querySelector('.app-section-toggle')?.setAttribute('aria-expanded', !collapsed)
            })
        } else {
            panel.classList.remove('app-section--accordion', 'collapsed')
        }

        this._bindConfigPanel(preset, presetId)
    }

    _renderConfigPanel(preset, plan, presetId) {
        const schema = preset.getSchema(plan)
        if (!schema.length) return ''
        const cfg    = this._presetConfigs[presetId] ?? {}
        const chevron = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`
        const fields = schema.map(field => {
            const val = cfg[field.key] ?? field.default
            if (field.type === 'color') {
                return `
                <div class="app-config-field">
                    <label class="app-config-label">${field.label}</label>
                    <input class="app-config-color" type="color" data-key="${field.key}" value="${val}">
                </div>`
            }
            if (field.type === 'range') {
                return `
                <div class="app-config-field">
                    <label class="app-config-label">${field.label} <span class="app-config-val">${val}</span></label>
                    <input class="app-config-range" type="range" data-key="${field.key}" min="${field.min}" max="${field.max}" step="${field.step ?? 1}" value="${val}">
                </div>`
            }
            if (field.type === 'select') {
                const opts = field.options.map(o => {
                    const v = o.value ?? o
                    const l = o.label ?? o
                    return `<option value="${v}"${String(val) === String(v) ? ' selected' : ''}>${l}</option>`
                }).join('')
                return `
                <div class="app-config-field">
                    <label class="app-config-label">${field.label}</label>
                    <select class="app-config-select" data-key="${field.key}">${opts}</select>
                </div>`
            }
            return ''
        }).join('')
        return `
    <div class="app-section-header">
        <h2 class="app-section-title">Customize</h2>
        <button class="app-section-toggle" aria-expanded="true">${chevron}</button>
    </div>
    <div class="app-section-body">
        <div class="app-config-fields">${fields}</div>
    </div>`
    }

    _bindConfigPanel(preset, presetId) {
        const panel = document.getElementById('configPanel')
        if (!panel) return
        panel.querySelectorAll('[data-key]').forEach(input => {
            input.addEventListener('input', () => {
                const key = input.dataset.key
                const val = input.value
                if (input.type === 'range') {
                    input.closest('.app-config-field').querySelector('.app-config-val').textContent = val
                }
                if (!this._presetConfigs[presetId]) this._presetConfigs[presetId] = {}
                this._presetConfigs[presetId][key] = val
                this._activePreset?.setConfig({ [key]: val })
                this._scheduleConfigSave()
            })
        })
    }

    _scheduleConfigSave() {
        clearTimeout(this._configSaveTimer)
        this._configSaveTimer = setTimeout(() => this._saveConfig(), 1500)
    }

    async _saveConfig() {
        try { localStorage.setItem('vyzflow_config', JSON.stringify(this._presetConfigs)) } catch (_) {}
        if (this._session) {
            await supabase.from('profiles')
                .update({ config: this._presetConfigs })
                .eq('id', this._session.user.id)
        }
    }

    _renderExportHistory(history) {
        if (!history.length) {
            return `<p class="app-exports-empty">No exports yet</p>`
        }
        const rows = history.map(e => {
            const d   = new Date(e.created_at)
            const date = `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
            return `
            <div class="app-export-row">
                <span class="app-export-preset">${e.preset_id}</span>
                <span class="app-export-format">${e.format}</span>
                <span class="app-export-date">${date}</span>
            </div>`
        }).join('')
        return `<div class="app-export-list">${rows}</div>`
    }
}
